import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { ArrowLeft, Loader2 } from "lucide-react";
import { API, getToken, clearToken, authHeaders } from "@/lib/api";
import { Monogram } from "@/components/Monogram";

const inputCls =
  "w-full border-b border-black/20 bg-transparent py-3 text-base font-light text-[#131311] outline-none transition-colors placeholder:text-zinc-400 focus:border-[#9A7B24]";

const Card = ({ title, children, testid }) => (
  <section data-testid={testid} className="border border-black/10 bg-white p-6 shadow-[0_20px_40px_-30px_rgba(19,19,17,0.25)] lg:p-8">
    <h2 className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#9A7B24]">{title}</h2>
    <div className="mt-6">{children}</div>
  </section>
);

export default function SettingsPage() {
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState(null);
  const [logs, setLogs] = useState(null);
  const [authed, setAuthed] = useState(!!getToken());
  const siteUrl = window.location.origin;

  useEffect(() => {
    if (!authed) return;
    const load = async () => {
      try {
        const [m, l] = await Promise.all([
          axios.get(`${API}/messages`, { headers: authHeaders() }),
          axios.get(`${API}/access-logs`, { headers: authHeaders() }),
        ]);
        setMessages(m.data.messages);
        setLogs(l.data.logs);
      } catch {
        setAuthed(false);
        clearToken();
      }
    };
    load();
  }, [authed]);

  const changePassword = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (pwForm.next.length < 4) {
      toast.error("New password must be at least 4 characters");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/gate/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      clearToken();
      setAuthed(false);
      setMessages(null);
      toast.success("Password updated — all visitors were signed out");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] px-6 py-10 lg:px-16">
      <div className="grain-overlay" />
      <header className="flex items-center justify-between">
        <Link
          data-testid="settings-back-link"
          to="/"
          className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 transition-colors hover:text-[#9A7B24]"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>
        <div className="flex items-center gap-3 text-zinc-600">
          <Monogram size={30} />
          <span className="font-mono text-[10px] uppercase tracking-[0.35em]">Owner Settings</span>
        </div>
      </header>

      <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Change Access Password" testid="settings-password-card">
          <p className="mb-6 text-sm font-light text-zinc-500">
            Set the password printed with your QR code cards. Changing it signs out every visitor instantly.
          </p>
          <form onSubmit={changePassword} className="flex flex-col gap-6">
            <input
              data-testid="settings-current-password-input"
              type="password"
              required
              placeholder="Current password"
              value={pwForm.current}
              onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="settings-new-password-input"
              type="password"
              required
              placeholder="New password (min. 4 characters)"
              value={pwForm.next}
              onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="settings-confirm-password-input"
              type="password"
              required
              placeholder="Confirm new password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              className={inputCls}
            />
            <button
              data-testid="settings-save-btn"
              type="submit"
              disabled={saving}
              className="group relative mt-2 self-start overflow-hidden rounded-full border border-[#B08D2E]/60 px-10 py-3 font-mono text-xs uppercase tracking-[0.3em] text-[#9A7B24] transition-colors duration-500 hover:text-white disabled:opacity-50"
            >
              <span className="absolute inset-0 -translate-x-full bg-[#B08D2E] transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative flex items-center gap-2">
                {saving && <Loader2 size={13} className="animate-spin" />}
                {saving ? "Saving…" : "Update Password"}
              </span>
            </button>
          </form>
        </Card>

        <Card title="Business Card QR" testid="settings-qr-card">
          <p className="mb-6 text-sm font-light text-zinc-500">
            This code opens your site. Print it on your cards with the current password — nothing else needed.
          </p>
          <div className="flex justify-center border border-black/10 bg-white px-8 py-10">
            <div className="flex flex-col items-center gap-5">
              <div className="text-[#B08D2E]">
                <Monogram size={100} />
              </div>
              <div className="bg-white p-2">
                <QRCode value={siteUrl} size={150} bgColor="#FFFFFF" fgColor="#131311" />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">royasmith.com</span>
            </div>
          </div>
          <p data-testid="qr-site-url" className="mt-4 text-center font-mono text-[10px] tracking-[0.2em] text-zinc-400">
            {siteUrl}
          </p>
          <a
            data-testid="qr-pdf-download-btn"
            href="/roya-smith-qr-card.pdf"
            download="roya-smith-qr-card.pdf"
            className="group relative mt-5 inline-flex overflow-hidden rounded-full border border-[#B08D2E]/60 px-8 py-2.5 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9A7B24] transition-colors duration-500 hover:text-white"
          >
            <span className="absolute inset-0 -translate-x-full bg-[#B08D2E] transition-transform duration-500 ease-out group-hover:translate-x-0" />
            <span className="relative">Download PDF card</span>
          </a>
        </Card>

        <Card title="Inbox" testid="settings-inbox-card">
          {!authed ? (
            <p data-testid="inbox-locked-note" className="text-sm font-light text-zinc-500">
              Enter the site password on the main page first, then return here to read messages.
            </p>
          ) : messages === null ? (
            <p className="font-mono text-xs text-zinc-400">Loading…</p>
          ) : messages.length === 0 ? (
            <p data-testid="inbox-empty" className="text-sm font-light text-zinc-500">
              No messages yet. New inquiries are also emailed to you.
            </p>
          ) : (
            <ul data-testid="inbox-list" className="flex max-h-96 flex-col gap-5 overflow-y-auto pr-2">
              {messages.map((m) => (
                <li key={m.id} className="border-b border-black/5 pb-4">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="font-display text-lg font-light italic text-zinc-800">{m.name}</span>
                    <span className="font-mono text-[10px] tracking-widest text-zinc-400">
                      {new Date(m.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="font-mono text-[10px] tracking-[0.15em] text-[#9A7B24]">{m.email}</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm font-light text-zinc-600">{m.message}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Recent Access" testid="settings-logs-card">
          {!authed ? (
            <p className="text-sm font-light text-zinc-500">Authenticate on the main page to view the access log.</p>
          ) : logs === null ? (
            <p className="font-mono text-xs text-zinc-400">Loading…</p>
          ) : (
            <ul data-testid="access-log-list" className="flex max-h-64 flex-col gap-2 overflow-y-auto">
              {logs.length === 0 && <li className="text-sm font-light text-zinc-500">No entries yet.</li>}
              {logs.map((l) => (
                <li key={l.id} className="flex justify-between border-b border-black/5 py-2 font-mono text-[10px] tracking-[0.15em] text-zinc-500">
                  <span className={l.event === "password_changed" ? "text-[#9A7B24]" : ""}>
                    {l.event === "gate_enter" ? "Vault opened" : "Password changed"}
                  </span>
                  <span>{new Date(l.at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
